import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import httpClient from 'Service'
import { toast } from 'react-toastify'
import { APP } from 'Constants/App'
import { IKoreanUniversity, KoreanUniversityPayload, normalizeKoreanUniversitiesList } from 'Entities/koreanUniversity'

const LIST_KEY = 'korean-universities-list'

const fetchUniversities = async (): Promise<IKoreanUniversity[]> => {
  const response = await httpClient.get<unknown>('/api/org/korean-universities')
  return normalizeKoreanUniversitiesList(response.data)
}

export const useKoreanUniversities = () => {
  const queryClient = useQueryClient()

  const listQuery = useQuery({
    queryKey: [LIST_KEY],
    queryFn: fetchUniversities,
  })

  const createMutation = useMutation({
    mutationFn: (payload: KoreanUniversityPayload) =>
      httpClient.post('/api/account/korean-universities', payload),
    onSuccess: () => {
      toast.success(APP.DATA_SUCCESS_SAVED)
      queryClient.invalidateQueries({ queryKey: [LIST_KEY] })
    },
    onError: () => {
      toast.error('Не удалось сохранить университет.')
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ universityId, payload }: { universityId: number; payload: KoreanUniversityPayload }) =>
      httpClient.patch(`/api/account/korean-universities/${universityId}`, payload),
    onSuccess: () => {
      toast.success(APP.DATA_SUCCESS_CHANGED)
      queryClient.invalidateQueries({ queryKey: [LIST_KEY] })
    },
    onError: () => {
      toast.error('Не удалось изменить университет.')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (universityId: number) => httpClient.delete(`/api/account/korean-universities/${universityId}`),
    onSuccess: () => {
      toast.success(APP.DATA_SUCCESS_REMOVED)
      queryClient.invalidateQueries({ queryKey: [LIST_KEY] })
    },
    onError: () => {
      toast.error('Не удалось удалить университет.')
    },
  })

  return {
    universities: listQuery.data ?? [],
    isUniversitiesLoading: listQuery.isLoading,
    isUniversitiesFetching: listQuery.isFetching,
    refetchUniversities: listQuery.refetch,
    createUniversityMutation: createMutation,
    updateUniversityMutation: updateMutation,
    deleteUniversityMutation: deleteMutation,
  }
}
